
import { GoogleGenAI, Type } from "@google/genai";
import { LogEntry, Project, WbsItem, ScopeAnalysis, ScopeClassification, CauseType, RecommendedAction } from "../types";

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || "dummy_key";
const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

export const getForensicBrief = async (project: Project, logs: LogEntry[]) => {
  const prompt = `
    You are a Senior Project Controls Forensic Auditor. 
    Summarize the current margin health of this construction project on Guam.
    
    PROJECT: ${project.name}
    CONTRACT: $${project.contractAmount}
    UNBILLED EXPOSURE: $${project.unbilledExposure}
    CPI: ${project.cpi}
    RECENT LOGS:
    ${logs.slice(0, 3).map(l => `- ${l.text}`).join('\n')}
    
    TASK: Provide a one-paragraph "Forensic Brief" (max 60 words).
    Focus on:
    1. Where is the profit eroding?
    2. What is the #1 recommended action this week to defend margin?
    
    Use firm, professional, and contractor-defensive language. No jargon.
  `;

  try {
    const model = ai.getGenerativeModel({ model: "gemini-1.5-flash" });
    const result = await model.generateContent(prompt);
    const response = await result.response;
    return response.text();
  } catch (e) {
    return "Forensic audit engine temporarily offline. Monitor unbilled exposure manually.";
  }
};

export const getChatbotInsight = async (query: string, project: Project, logs: LogEntry[]) => {
  const prompt = `
    You are the "MarginGuard Intelligence Bot". Your specific mission is to advise a construction owner on Guam (commercial projects <$20M, lean 3-person team) on how to protect their profit margins.
    
    PROJECT CONTEXT:
    Name: ${project.name}
    CPI: ${project.cpi}
    Unbilled Exposure: $${project.unbilledExposure}
    Margin Status: ${project.marginStatus}
    
    RECENT FIELD FRICTION:
    ${logs.slice(0, 5).map(l => `- ${l.date}: ${l.text}`).join('\n')}
    
    USER QUERY: ${query}
    
    Provide a concise, sharp, and proactive insight. Focus on:
    1. Identifying quiet margin bleed.
    2. Suggesting defensible change order strategies.
    3. Managing subcontractor performance risks.
    4. Keep it grounded in the Guam context (limited specialized labor, small project scale).
    
    Response should be in plain text, assertive, and under 100 words.
  `;

  const model = ai.getGenerativeModel({ model: "gemini-1.5-flash" });
  const result = await model.generateContent(prompt);
  const response = await result.response;
  return response.text();
};

export const auditLogWithGemini = async (
  log: LogEntry,
  project: Project,
  wbsItems: WbsItem[]
): Promise<ScopeAnalysis> => {
  const prompt = `
    You are a high-stakes construction claims and project controls specialist advising a small, lean managing team (3 people) on the island of Guam.
    Audit this daily log for uncompensated scope creep and subcontractor margin drain on a private/commercial project up to $20M.
    
    MISSION: PREVENT QUIET MARGIN BLEED. 
    Every undocumented instruction is a loss. Every unforeseen condition is a Change Order opportunity.
    
    PROJECT: ${project.name}
    CONTRACT AMOUNT: $${project.contractAmount}
    CURRENT CPI: ${project.cpi}
    MARGIN STATUS: ${project.marginStatus}

    WBS / CONTRACT BASELINE:
    ${wbsItems.map(w => `[ID: ${w.id}] ${w.code} - ${w.title}: ${w.description} (${w.scopeType})`).join('\n')}

    FIELD LOG ENTRY:
    Text: ${log.text}
    Tags: ${log.tags.join(', ')}

    INSTRUCTIONS:
    1. Classify scope: "InScope", "OutOfScopeLikely", or "Ambiguous". 
    2. Suggest a "RecommendedAction": "DraftCO" if work is uncompensated, "ClarifyViaRFI" if ambiguous, or "NoAction".
    3. EXPLICITLY EVALUATE MARGIN RISK. If the log indicates subcontractor friction, standby, or owner-directed changes, highlight the risk.
    4. "financialImpactRationale": Write a sharp, owner-defensive justification. Use plain, assertive language. Mention that on Guam, specialized labor availability makes these delays particularly costly.
  `;

  const model = ai.getGenerativeModel({
    model: "gemini-1.5-pro",
    generationConfig: {
      responseMimeType: "application/json",
      // responseSchema is not directly supported in the same way in all versions, 
      // but we can pass the prompt instructions.
    }
  });

  const result = await model.generateContent(prompt);
  const response = await result.response;
  const text = response.text() || '{}';
  const data = JSON.parse(text);
  return {
    ...data,
    logId: log.id
  };
};

export const generateCoDraft = async (
  log: LogEntry,
  analysis: ScopeAnalysis,
  wbsContext: WbsItem[]
) => {
  const prompt = `
    You are a senior claims specialist writing for a lean 3-person management team. 
    Produce a firm, contractor-favorable Change Order justification that is ready to be sent.
    
    LOG: ${log.text}
    FORENSIC BASIS: ${analysis.financialImpactRationale}
    CONTRACT BASELINE: ${wbsContext.map(w => `${w.code}: ${w.title}`).join(', ')}
    
    Style Guidelines:
    - No fluff or jargon.
    - Fact-based and assertive.
    - Reference specific activities and baseline scope exclusions.
    - Explicitly state that this work constitutes a change to the contract requirements.
    
    Structure the response in JSON with "title", "summary", and "narrative".
  `;

  const model = ai.getGenerativeModel({
    model: "gemini-1.5-pro",
    generationConfig: {
      responseMimeType: "application/json",
    }
  });

  const result = await model.generateContent(prompt);
  const response = await result.response;
  const text = response.text() || '{}';
  return JSON.parse(text);
};
